/**
 * Schematic ports — signal, bus, power and ground.
 *
 * CIRCAD's help says it plainly: "the bus port that is created is actually a
 * special case of a component". Every one of these is a `$$`-named component
 * holding one pad, and the pad's `Signal:` is the whole of the connection. The
 * drawing is on the Symbols layer and conducts nothing; the label is the marker
 * `&@`, which renders as the pad's signal, so renaming the net renames the
 * label with it.
 *
 * That is also how a bus works. A bus wire is a fat line on the Busses layer —
 * a *drawing* layer, joining nothing — and the signals that ride it are joined
 * by name, one bus port per tap. Nothing else is needed and nothing else was
 * ever there: `netlistout` groups by signal name, and so do we.
 *
 * The geometry is transcribed from the original's own blocks, `CmpSignal`,
 * `CmpBus`, `CmpGround`, `CmpEarth`, `CmpVoltage`, `CmpPplus` and `CmpPminus`
 * at OG0.cpp:4355-4437, in inches, down to the .009" pad and the .01" stroke.
 */

import {
  LayerType, newId,
  type CadDocument, type ComponentEntity, type Entity, type TextAnchor,
} from '../model.ts';
import { measure } from '../font/text.ts';

/** Which port to build. The original's Place menu, one for one. */
export type PortKind = 'signal' | 'bus' | 'gnd' | 'earth' | 'named-gnd' | 'power';

/** The three schematic layers a port is drawn across. */
export interface SchLayers {
  /** Outlines and lettering. Draws, conducts nothing. */
  symbols: number;
  /** The one Schematic-type layer: wires and pins. */
  signals: number;
  /** Bus wires. A drawing layer, so a bus crossing a wire is not a short. */
  busses: number;
}

/**
 * Where the ports go on this sheet.
 *
 * CIRCAD hardcodes 1, 2 and 3 (OGmenu02.cpp:983, 1102) because SCH.LIB's stack
 * is fixed. Ours may have been fitted to an imported sheet, so the layers are
 * found by type and name with the original's numbers as the fallback.
 */
export function schLayers(doc: CadDocument): SchLayers {
  const byName = (re: RegExp): number =>
    doc.layers.findIndex((l) => re.test(l.name));
  const signals = doc.layers.findIndex((l) => l.type === LayerType.Schematic);
  const symbols = doc.layers.findIndex((l) => l.type === LayerType.Symbols);
  const busses = byName(/bus/i);
  return {
    symbols: symbols >= 0 ? symbols : 1,
    signals: signals >= 0 ? signals : 2,
    busses: busses >= 0 ? busses : (symbols >= 0 ? symbols : 3),
  };
}

/** The `Signal:` a port of this kind carries, given what the operator typed. */
export function portSignal(kind: PortKind, name: string): string {
  if (kind === 'gnd') return 'GND';
  if (kind === 'earth') return 'EARTH';
  return name.trim();
}

/** What to call the placement, in a message or on the undo stack. */
export const PORT_LABELS: Record<PortKind, string> = {
  signal: 'Signal Port',
  bus: 'Bus Port',
  gnd: 'Signal GND',
  earth: 'Earth GND',
  'named-gnd': 'Named GND',
  power: 'Power Port',
};

/**
 * Increment the trailing number of a name, as `plincr` does (OGmenu00.cpp:1367).
 *
 * This is what makes a bus bearable: place `D0` and the next port offers `D1`,
 * so an eight-bit bus is eight clicks rather than eight typings. Carries like
 * arithmetic — `D9` becomes `D10`, `A09` becomes `A10` — and a name with no
 * digits in it is returned unchanged.
 */
export function nextPortName(name: string): string {
  const m = /^(.*?)(\d+)(\D*)$/.exec(name);
  if (!m) return name;
  const [, head, digits, tail] = m as unknown as [string, string, string, string];
  const next = String(Number(digits) + 1);
  // Keep the field width where it was zero-padded: A09 -> A10, not A9+1.
  const padded = next.length < digits.length
    ? next.padStart(digits.length, '0')
    : next;
  return `${head}${padded}${tail}`;
}

/**
 * Build a port, ready to place.
 *
 * `net` is the signal name; for the two fixed grounds it is ignored and the
 * original's own name is used. The label is `&@` throughout rather than the
 * name spelled out, so the two can never drift apart.
 */
export function buildPort(
  kind: PortKind, net: string, layers: SchLayers, at = { x: 0, y: 0 },
): ComponentEntity {
  const signal = portSignal(kind, net);
  const SYM = layers.symbols;
  const children: Entity[] = [];

  const line = (x1: number, y1: number, x2: number, y2: number): void => {
    children.push({ kind: 'line', id: newId(), layer: SYM, width: 0.01, x1, y1, x2, y2 });
  };
  const label = (x: number, y: number, anchor: TextAnchor): void => {
    children.push({
      kind: 'text', id: newId(), layer: SYM, x, y,
      height: 0.06, aspect: 1, anchor, text: '&@',
    });
  };
  // The pin. On the Signals layer, as the original's blocks are: it is the one
  // part of a port that connects, and everything else is lettering.
  const pin = (): void => {
    children.push({
      kind: 'pad', id: newId(), layer: layers.signals, type: 'T0',
      x: 0, y: 0, sizeX: 0.009, sizeY: 0.009, signal,
    });
  };

  let bounds: { x1: number; y1: number; x2: number; y2: number };
  let name: string;

  switch (kind) {
    case 'signal': {
      // The flag stretches to fit its name. `placepart` measures the string and
      // shifts every vertex left of the text origin by that much
      // (OGmenu00.cpp:1288-1310), so a port called `RESET_N` is a long flag and
      // one called `A` is a short one — the outline always fits the word.
      name = '$$SIG';
      const w = measure(signal) * 0.06;
      label(-0.075 - w, 0, 'ML');
      pin();
      line(0, 0, -0.045, 0.045);
      line(-0.045, 0.045, -0.1 - w, 0.045);
      line(-0.1 - w, 0.045, -0.1 - w, -0.045);
      line(-0.1 - w, -0.045, -0.045, -0.045);
      line(-0.045, -0.045, 0, 0);
      bounds = { x1: -0.1 - w, y1: -0.045, x2: 0, y2: 0.045 };
      break;
    }
    case 'bus': {
      // A stub and a 45° riser: the pin sits on the signal wire and the riser
      // points up into the bus. Its label rides above the stub.
      name = '$$BUS';
      label(0.05, 0, 'BR');
      pin();
      line(0, 0, 0.1, 0);
      line(0.1, 0, 0.2, 0.1);
      bounds = { x1: 0, y1: 0, x2: 0.2, y2: 0.1 };
      break;
    }
    case 'gnd': {
      name = '$$GND';
      pin();
      line(-0.075, 0, 0.075, 0);
      line(-0.05, -0.025, 0.05, -0.025);
      line(-0.025, -0.05, 0.025, -0.05);
      bounds = { x1: -0.075, y1: -0.05, x2: 0.075, y2: 0 };
      break;
    }
    case 'earth': {
      name = '$$GND';
      pin();
      line(-0.075, 0, 0.075, 0);
      line(0.075, 0, 0.05, -0.075);
      line(0, 0, -0.025, -0.075);
      line(-0.075, 0, -0.1, -0.075);
      bounds = { x1: -0.1, y1: -0.075, x2: 0.075, y2: 0 };
      break;
    }
    case 'named-gnd': {
      name = '$$GND';
      label(0, -0.12, 'MC');
      pin();
      line(0, -0.075, 0.075, 0);
      line(0.075, 0, -0.075, 0);
      line(-0.075, 0, 0, -0.075);
      // Down to the label, not to the triangle: bounds are what the editor
      // picks and drags by, and a name you cannot grab is a name you cannot fix.
      bounds = { x1: -0.075, y1: -0.16, x2: 0.075, y2: 0 };
      break;
    }
    default: {
      // `+/- Port`. A leading minus points the arrow down, which is the
      // original's own test (OGmenu00.cpp:1201).
      name = '$$PWR';
      const s = signal.startsWith('-') ? -1 : 1;
      label(0, 0.1 * s, 'MC');
      pin();
      line(0, 0, 0, 0.05 * s);
      line(-0.02, 0.01 * s, 0, 0.05 * s);
      line(0.02, 0.01 * s, 0, 0.05 * s);
      bounds = s > 0
        ? { x1: -0.025, y1: 0, x2: 0.025, y2: 0.14 }
        : { x1: -0.025, y1: -0.14, x2: 0.025, y2: 0 };
      break;
    }
  }

  return {
    kind: 'component', id: newId(), layer: SYM, name,
    bounds, x: at.x, y: at.y, params: {}, children,
  };
}

/** True for one of the `$$` port blocks — not a placeable package. */
export function isPortComponent(c: ComponentEntity): boolean {
  return c.name.startsWith('$$');
}
